import React, { useEffect, useRef } from 'react';

function SVGThumbnail({ filePath, width = 200, height = 150 }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !filePath) return;

    // Clear previous content
    containerRef.current.innerHTML = '';

    let objectUrl = null;

    // Construct full API URL
    let apiUrl = filePath;
    if (filePath.startsWith('/jobs/') || filePath.startsWith('/laser-jobs/')) {
      const baseURL = process.env.NODE_ENV === 'development'
        ? (process.env.REACT_APP_API_BASE || '/api')
        : '/hub/api';
      apiUrl = baseURL + filePath;
    }

    fetch(apiUrl)
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch file');
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('svg')) {
          return response.text().then(svgText => ({ type: 'svg', data: svgText }));
        }
        if (contentType.startsWith('image/')) {
          return response.blob().then(blob => ({ type: 'image', data: blob }));
        }
        throw new Error('Unsupported file type');
      })
      .then(({ type, data }) => {
        // Create a wrapper div to contain the preview
        const wrapper = document.createElement('div');
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';
        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.justifyContent = 'center';
        wrapper.style.backgroundColor = '#f5f5f5';
        wrapper.style.borderRadius = '4px';
        wrapper.style.overflow = 'hidden';

        if (type === 'svg') {
          // Parse and set SVG
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(data, 'text/xml');
          const svgElement = svgDoc.documentElement;

          if (svgElement.tagName !== 'svg') {
            throw new Error('Invalid SVG file');
          }

          // Set viewBox if not present
          if (!svgElement.hasAttribute('viewBox')) {
            const width = svgElement.getAttribute('width') || '100';
            const height = svgElement.getAttribute('height') || '100';
            svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
          }

          // Set responsive dimensions
          svgElement.setAttribute('width', '100%');
          svgElement.setAttribute('height', '100%');
          svgElement.style.maxWidth = '100%';
          svgElement.style.maxHeight = '100%';

          wrapper.appendChild(svgElement);
        } else {
          objectUrl = URL.createObjectURL(data);
          const img = document.createElement('img');
          img.src = objectUrl;
          img.style.maxWidth = '100%';
          img.style.maxHeight = '100%';
          img.style.objectFit = 'contain';
          wrapper.appendChild(img);
        }

        containerRef.current.appendChild(wrapper);
      })
      .catch(err => {
        console.error('Error loading file preview:', err);
        showFallback();
      });

    function showFallback() {
      if (containerRef.current) {
        containerRef.current.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; background-color: #f5f5f5; color: #999; font-size: 12px; border-radius: 4px;">📄 File</div>';
      }
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filePath, width, height]);

  return (
    <div
      ref={containerRef}
      style={{
        width,
        height,
        overflow: 'hidden',
        borderRadius: '4px',
        backgroundColor: '#f5f5f5'
      }}
    />
  );
}

export default SVGThumbnail;
